import { debugEmailToken } from '@news-mono/web-common'
import { useEffect, useState } from 'react'
import { useMutation, useQuery } from 'react-query'
import url from 'url'
import { EmailPreferenceItem } from '../components/PreferenceSection'
import {
    perthnowEmailPreferenceContent,
    thewestEmailPreferenceContent,
} from '../config'
import { UnsubscribeParam } from '../pages/Unsubscribe'

export function useValidateIdAndToken(param?: UnsubscribeParam) {
    const [error, setError] = useState<false | string>(false)
    const [subscribeAccount, setSubscribeAccount] =
        useState<EmailPreferenceItem>()
    useEffect(() => {
        if (param) {
            const { listId, unsubId, tokens } = param
            const hasListId = listId && typeof listId === 'string'
            const hasUnsubId = unsubId && typeof unsubId === 'string'
            const hasToken =
                tokens.urlToken && typeof tokens.urlToken === 'string'
            if ((!hasListId && !hasUnsubId) || !hasToken) {
                setError('Invalid link.')
                return
            }
            // Validate list id
            const subscribeAccount = validateListId(listId, unsubId)
            if (!subscribeAccount) {
                setError('Invalid List Id.')
                return
            }
            setError(false)
            setSubscribeAccount(subscribeAccount)
        }
    }, [param])

    // Return unsubscribe account data
    return { subscribeAccount, error }
}

export function getQuery() {
    const { listId, unsubId, token } = url.parse(
        window.location.href,
        true,
    ).query
    const hasListId = listId && typeof listId === 'string'
    const hasUnsubId = unsubId && typeof unsubId === 'string'
    const hasToken = token && typeof token === 'string'

    if ((!hasListId && !hasUnsubId) || !hasToken) {
        throw new Error('Invalid link.')
    }
    return { listId, unsubId, tokens: { urlToken: token } } as UnsubscribeParam
}

export function useResolveEmail(
    useProfileEmail: boolean,
    emailServiceApi?: string,
    emailToken?: string,
    userEmail?: string,
) {
    const api = `${emailServiceApi}/email-preference-center/resolve-email`
    const { isLoading, error, data } = useQuery(
        'resolveEmail',
        async () => {
            if (emailToken) {
                const response = await fetch(api, {
                    headers: {
                        Authorization: emailToken,
                    },
                })
                // If the token is invalid we resolve to undefined
                if (response.status === 401) {
                    debugEmailToken('Invalid preference centre token provided')
                    return
                }
                if (!response.ok) {
                    throw new Error('Network response was not ok')
                }
                return (await response.json()) as { email: string }
            }
        },
        {
            refetchOnWindowFocus: false,
            enabled: !!emailToken,
        },
    )

    return {
        isLoading,
        resolveEmailError: error,
        data: {
            tokenEmail: data?.email,
            profileEmail: userEmail,
        },
    }
}

export function useSingleUnsubscribe(
    payload: {
        account?: string
        listId?: string
        toggleState: 'off'
        unsubscribeGroupId?: number
    },
    token?: string,
    emailServiceApi?: string,
) {
    const { account, listId, unsubscribeGroupId } = payload
    return useMutation('toggle-email-preference', async () => {
        const api = `${emailServiceApi}/email-preference-center/toggle-preference`
        if (account && listId && unsubscribeGroupId && token) {
            const response = await fetch(api, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: token,
                },
                body: JSON.stringify(payload),
            })
            if (!response.ok) {
                throw new Error('Network response was not ok')
            }
            return true
        }
    })
}

function validateListId(listId?: string, unsubId?: string) {
    const subscribeAccounts: EmailPreferenceItem[] = []
    thewestEmailPreferenceContent.forEach((section) =>
        subscribeAccounts.push(...section.sectionItems),
    )
    perthnowEmailPreferenceContent.forEach((section) =>
        subscribeAccounts.push(...section.sectionItems),
    )

    return subscribeAccounts.find((account) => {
        return unsubId
            ? account.unSubId.toString() === unsubId
            : account.guid === listId
    })
}

export function getStorage(key: string) {
    try {
        const storageData = sessionStorage && sessionStorage.getItem(key)
        if (!storageData) return null
        const parsedData = JSON.parse(storageData)
        return parsedData
    } catch (error) {
        return null
    }
}
